import { ChevronDown } from 'lucide-react';
import React, { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion/accordion.tsx';
import { NumberOrEmpty } from '../../../components/typography/number-or-empty.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import Text from '../../../components/ui/text.tsx';
import { cn } from '../../../lib/utils.ts';
import { IntercomLauncher } from '../../intercom/components/intercom-launcher.tsx';
import type { MenuItem } from '../contracts.ts';
import { useNavGroupCollapse } from '../hooks/use-nav-group-collapse.ts';
import { usePlatoLayoutNavigation } from '../hooks/use-plato-layout-navigation.tsx';
import {
    createGetItemClassname,
    createGetTopLevelItemClassname,
    createIsSelected,
    createIsSelectedOnlyTopLevel,
    ITEM_ICON_CLASSNAME,
} from './navigation-helpers.ts';
import { SettingsMenu } from './settings-menu.tsx';

export type NavigationListProps = {
    closeDrawer: () => void;
};

export function NavigationList({ closeDrawer }: NavigationListProps) {
    const location = useLocation();
    const { navigation, activeSection } = usePlatoLayoutNavigation();
    const { isCollapsed, toggle } = useNavGroupCollapse();

    const isSelected = createIsSelected(activeSection, location.pathname);
    const isSelectedOnlyTopLevel = createIsSelectedOnlyTopLevel(activeSection, isSelected);
    const getItemClassname = createGetItemClassname(location.pathname, isSelected);
    const getTopLevelItemClassname = createGetTopLevelItemClassname(
        location.pathname,
        isSelectedOnlyTopLevel,
    );

    const renderItem = (item: MenuItem): ReactNode => (
        <>
            {!item.subItems && !!item.href && (
                <Link to={item.href} onClick={closeDrawer} className={getItemClassname(item)}>
                    <item.icon className={ITEM_ICON_CLASSNAME} aria-hidden="true" />

                    <Text color="inherit">{item.name}</Text>

                    {!!item.count && (
                        <div className="flex grow justify-end">
                            <Badge
                                variant="destructive"
                                className="h-4 min-w-4 justify-center rounded-full px-1 py-0.5 text-[10px] font-normal"
                            >
                                <NumberOrEmpty>{item.count}</NumberOrEmpty>
                            </Badge>
                        </div>
                    )}
                </Link>
            )}
            {!!item.subItems && (
                <Accordion
                    type="single"
                    collapsible
                    defaultValue={isSelectedOnlyTopLevel(item) ? item.id : undefined}
                >
                    <AccordionItem value={item.id}>
                        <AccordionTrigger className={getTopLevelItemClassname(item)}>
                            <item.icon className={ITEM_ICON_CLASSNAME} aria-hidden="true" />
                            <Text className="w-full text-left" color="inherit">
                                {item.name}
                            </Text>
                        </AccordionTrigger>
                        <AccordionContent>
                            <ul className="mt-2 flex w-full flex-col items-start gap-3">
                                {item.subItems.map((subItem) => (
                                    <li key={subItem.id} className="flex w-full flex-row gap-3">
                                        <div className="w-6 shrink-0" aria-hidden="true" />
                                        <Link
                                            onClick={closeDrawer}
                                            to={subItem.href}
                                            className={getItemClassname(subItem)}
                                        >
                                            <subItem.icon
                                                className={ITEM_ICON_CLASSNAME}
                                                aria-hidden="true"
                                            />
                                            <Text color="inherit">{subItem.name}</Text>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            )}
        </>
    );

    return (
        <>
            <nav className="w-full">
                <ul className="flex w-full flex-col items-start gap-3">
                    {navigation.map((group) =>
                        group.label === undefined ? (
                            // Unlabelled group (the rep menu, the manager launchpad):
                            // bare items, exactly today's markup.
                            group.items.map((item) => (
                                <React.Fragment key={item.id}>
                                    <li key={item.id} className="w-full">
                                        {renderItem(item)}
                                    </li>
                                </React.Fragment>
                            ))
                        ) : (
                            // Labelled group (manager-only): collapsible section.
                            <li key={group.id} className="mt-3 w-full">
                                <button
                                    type="button"
                                    aria-expanded={!isCollapsed(group.id)}
                                    onClick={() => {
                                        toggle(group.id);
                                    }}
                                    className={cn(
                                        'flex h-6 w-full items-center justify-between rounded-md px-3',
                                        'text-[11px] font-semibold uppercase tracking-wide',
                                        'text-neutral-400 hover:text-neutral-600',
                                    )}
                                >
                                    <span>{group.label}</span>
                                    <ChevronDown
                                        className={cn(
                                            'h-3 w-3 shrink-0 transition-transform',
                                            isCollapsed(group.id) && '-rotate-90',
                                        )}
                                        aria-hidden="true"
                                    />
                                </button>
                                {!isCollapsed(group.id) && (
                                    <ul className="mt-1 flex w-full flex-col items-start gap-3">
                                        {group.items.map((item) => (
                                            <li key={item.id} className="w-full">
                                                {renderItem(item)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ),
                    )}
                </ul>
            </nav>
            <div className="grow"></div>
            <ul className="flex w-full flex-col items-end gap-3">
                <li className="w-full">
                    <IntercomLauncher />
                </li>
                <li className="w-full">
                    <SettingsMenu />
                </li>
            </ul>
        </>
    );
}
